import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, Package, Truck } from "lucide-react";

/**
 * Merch order thank-you page. Handles BOTH providers:
 *  - Stripe:  ?session_id=cs_...
 *  - PayPal:  ?provider=paypal&token=<orderId>
 * Polls the matching status endpoint until the payment is confirmed.
 */
export default function MerchThanks() {
  const [params] = useSearchParams();
  const provider = params.get("provider") === "paypal" ? "paypal" : "stripe";
  const sessionId = params.get("session_id");
  const orderId = params.get("token") || params.get("order_id");
  const key = provider === "paypal" ? orderId : sessionId;

  const [status, setStatus] = useState({ payment_status: "pending" });
  const [ticks, setTicks] = useState(0);

  useEffect(() => {
    if (!key || ticks > 40) return;
    const t = setTimeout(async () => {
      try {
        const url = provider === "paypal" ? `/merch/paypal/status/${orderId}` : `/merch/status/${sessionId}`;
        const { data } = await api.get(url);
        setStatus(data);
        if (data.payment_status === "paid") return;
      } catch { /* retry */ }
      setTicks((n) => n + 1);
    }, 2000);
    return () => clearTimeout(t);
  }, [key, ticks, provider, orderId, sessionId]);

  const paid = status.payment_status === "paid";

  return (
    <div className="max-w-[720px] mx-auto px-6 lg:px-12 py-24" data-testid="merch-thanks-page">
      <div className="surface-card p-10 text-center">
        {!key ? (
          <>
            <Package size={56} className="mx-auto text-[var(--ff-text-dim)] mb-4" />
            <h1 className="font-display text-3xl mb-3">ORDER NOT FOUND</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm">We couldn&apos;t find your order reference.</p>
          </>
        ) : !paid ? (
          <>
            <Loader2 size={64} className="mx-auto text-[var(--ff-red)] mb-4 animate-spin" />
            <h1 className="font-display text-3xl mb-3">CONFIRMING PAYMENT…</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm">
              Waiting for {provider === "paypal" ? "PayPal" : "Stripe"} to confirm your order. This usually takes a moment.
            </p>
          </>
        ) : (
          <>
            <CheckCircle2 size={72} className="mx-auto text-green-400 mb-4" data-testid="merch-thanks-paid" />
            <h1 className="font-display text-4xl mb-3">ORDER CONFIRMED!</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm mb-6">
              Thanks for grabbing {status.item_name ? <b className="text-white">{status.item_name}</b> : "your merch"}.
            </p>
            <div className="flex items-start gap-3 text-left bg-[var(--ff-bg)] border border-[var(--ff-border)] p-4 rounded mb-6">
              <Truck size={20} className="text-[var(--ff-text-dim)] shrink-0 mt-0.5" />
              <p className="text-[var(--ff-text-dim)] font-mono-ui text-xs leading-relaxed">
                We&apos;ve got your order. If it ships, you&apos;ll get an update once it&apos;s on the way.
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <Link to="/shows" className="btn-primary" data-testid="merch-thanks-shows">Browse Shows</Link>
              <Link to="/" className="btn-ghost" data-testid="merch-thanks-home">Back Home</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
